import React, {
	createContext,
	Dispatch,
	UIEvent,
	useCallback,
	useContext,
	useRef,
	useState,
} from 'react';

import AccountPanel, {
	EAccountDisplayState,
} from '@/components/AccountPanel/AccountPanel';
import { AddCustomSong } from '@/components/Layout/components/AddCustomSong';
import {
	AddSongs,
	AddSongsConfig,
} from '@/components/Layout/components/AddSongs';
import {
	GenerateSetlist,
	GenerateSetlistProps,
} from '@/components/Layout/components/GenerateSetlist';
import {
	GenerateSettingsBottomPanel,
	GenerateSettingsBottomPanelProps,
} from '@/components/Layout/components/GenerateSettingsBottomPanel/GenerateSettingsBottomPanel';
import { QuickAddSongsBottomPanel } from '@/components/Layout/components/QuickAddSongsBottomPanel/QuickAddSongsBottomPanel';
import { ImportCsv } from '@/components/Layout/components/ImportCsv';
import {
	SonglistModalPanel,
	SonglistModalPanelProps,
} from '@/components/Layout/components/SonglistModalPanel';
import {
	SongModalPanel,
	SongModalPanelProps,
} from '@/components/Layout/components/SongModalPanel';
import { RenderRichText } from '@/components/Layout/components/SongModalPanel/SongModalRoot';
import styles from '@/components/Layout/Layout.module.scss';
import {
	BottomCard,
	BottomCardAction,
	BottomCardForm,
} from '@/components/shared/BottomCard';
import { AsaphButton } from '@/components/shared/Button';
import { SupportUsModal } from '@/components/SupportUsModal';
import { useGetReelFeedQuery } from '@/graphql';
import { classNames } from '@/helpers/classNames';
import { debounce } from '@/helpers/debounce';
import { useAnalyticsEvent } from '@/providers/AnalyticsProvider';
import { useAuth } from '@/providers/AuthProvider';
import { usePermissions } from '@/providers/PermissionsProvider';
import { useUnreadChats } from '@/providers/UnreadChatsProvider/UnreadChatsProvider';
import { isNil } from 'ramda';
import Fireworks from 'react-canvas-confetti/dist/presets/realistic';
import ReactDOM from 'react-dom';
import { useLocation } from 'react-router';

type AccountOpenState = {
	open: boolean;
	defaultDisplayState?: EAccountDisplayState;
};

export type ILayoutOpenSong = (config: SongModalPanelProps['config']) => void;

export type ILayoutOpenSonglistWithCallback = (
	callbackFn: SonglistModalPanelProps['callbackFn'],
	config?: SonglistModalPanelProps['config'],
) => void;

export type ILayoutOpenSetlistWithCallback = (
	callbackFn: GenerateSetlistProps['callbackFn'],
	config: GenerateSetlistProps['config'],
) => void;

export type BottomCardConfig = {
	title: string;
	text: string;
	component?: React.ReactNode;
	callbackFn?: () => void;
};

export type OpenAddSongsConfig = AddSongsConfig & {
	callbackFn?: (addedSongIds: string[]) => void;
};

export interface ILayoutProps {
	setAccountOpen: Dispatch<AccountOpenState>;
	openSong: ILayoutOpenSong;
	openSonglistWithCallback: ILayoutOpenSonglistWithCallback;
	openSetlistWithCallback: ILayoutOpenSetlistWithCallback;
	openSetlistGenerate: (
		config?: GenerateSettingsBottomPanelProps['config'],
	) => void;
	openAddSongs: (config?: OpenAddSongsConfig) => void;
	openAddCustomSong: () => void;
	openImportCsv: () => void;
	openBottomCard: (config: BottomCardConfig) => void;
	openQuickAddSongs: () => void;
	setHideBottomMenu: Dispatch<boolean>;
	shootFireworks: () => void;
	openSupportModal: () => void;
	hideBottomMenu: boolean;
	scrollTop?: number;
}

export const LayoutContext = createContext<ILayoutProps>({
	setAccountOpen: () => null,
	openSong: () => null,
	openSonglistWithCallback: () => null,
	openSetlistWithCallback: () => null,
	openSetlistGenerate: () => null,
	openAddSongs: () => null,
	openAddCustomSong: () => null,
	openImportCsv: () => null,
	openBottomCard: () => null,
	openQuickAddSongs: () => null,
	setHideBottomMenu: () => null,
	shootFireworks: () => null,
	openSupportModal: () => null,
	hideBottomMenu: false,
	scrollTop: 0,
});

type ConfettiConductor = {
	shoot: () => void;
	run: (options: { speed: number }) => void;
	pause: () => void;
};

export function useLayout(): ILayoutProps {
	const context = useContext(LayoutContext);
	if (isNil(context)) {
		throw new Error(`useLayout must be used within a LayoutProvider`);
	}

	return context;
}

export const LayoutProvider: React.FC = ({ children }) => {
	const { chatIds, videoChatIds } = useUnreadChats();
	const { isCopilot } = useAuth();

	const fireNavigateGenerateSetlist = useAnalyticsEvent(
		'Generate_Setlist_Bottom_Panel_Nagivate_Generate_Setlist',
	);
	const fireCloseGenerateSetlistBottomPanel = useAnalyticsEvent(
		'Generate_Setlist_Bottom_Panel_Close',
	);
	const fireOpenQuickAddSongsEvent = useAnalyticsEvent(
		'Layout_Open_Quick_Add_Songs_Bottom_Panel',
	);
	const controller = useRef<ConfettiConductor | null>(null);

	const [accountOpen, setAccountOpen] = useState<AccountOpenState>({
		open: false,
	});
	const [openedSong, setOpenedSong] = useState(false);
	const [openedSonglist, setOpenedSonglist] = useState(false);
	const [openedSetlist, setOpenedSetlist] = useState(false);
	const [openedSupportModal, setOpenedSupportModal] = useState(false);
	const [openedGenerateSetlist, setOpenedGenerateSetlist] = useState(false);
	const [openedAddSongs, setOpenedAddSongs] = useState(false);
	const [openedAddCustomSong, setOpenedAddCustomSong] = useState(false);
	const [openedImportCsv, setOpenedImportCsv] = useState(false);

	const { refetch } = useGetReelFeedQuery();

	const [songlistCallbackFn, setSonglistCallbackFn] = useState<
		SonglistModalPanelProps['callbackFn']
	>(() => () => {});
	const [setlistCallbackFn, setSetlistCallbackFn] = useState<
		GenerateSetlistProps['callbackFn']
	>(() => () => {});
	// If the above looks weird, check out https://medium.com/swlh/how-to-store-a-function-with-the-usestate-hook-in-react-8a88dd4eede1
	const [songConfig, setSongConfig] = useState<
		SongModalPanelProps['config'] | undefined
	>();
	const [songlistConfig, setSonglistConfig] =
		useState<SonglistModalPanelProps['config']>();
	const [setlistConfig, setSetlistConfig] =
		useState<GenerateSetlistProps['config']>();
	const [generateSetlistConfig, setGenerateSetlistConfig] =
		useState<GenerateSettingsBottomPanelProps['config']>();
	const [addSongsConfig, setAddSongsConfig] = useState<OpenAddSongsConfig>();

	const [openedBottomCard, setOpenedBottomCard] = useState(false);
	const [bottomCardConfig, setBottomCardConfig] = useState<
		BottomCardConfig | undefined
	>();

	const [openedQuickAddSongs, setOpenedQuickAddSongs] = useState(false);

	// Enable this to auto-open a dialog
	// useEffect(() => {
	// 	openSetlistWithCallback(() => {}, {
	// 		chatId: '1',
	// 		selectedSongIds: ['112', '113', '114'],
	// 		viewOnly: false,
	// 	});
	// }, []);

	const [scrollTop, setScrollTop] = useState<number>(0);
	const [hideBottomMenu, setHideBottomMenu] = useState<boolean>(false);
	const { permissions } = usePermissions();
	const scrollbarRef = useRef<HTMLDivElement>(null);

	const fireOpenAddSongsEvent = useAnalyticsEvent('Songlist_Open_Add_Songs');
	const fireOpenAddCustomSongEvent = useAnalyticsEvent(
		'Songlist_Open_Add_Custom_Song',
	);

	const onScroll = (e: UIEvent<HTMLElement>) =>
		setScrollTop((e.target as HTMLElement).scrollTop);
	const debouncedOnScroll = debounce(onScroll, 100);

	const location = useLocation();

	const openSong = useCallback((config: SongModalPanelProps['config']) => {
		setSongConfig(config);
		setOpenedSong(true);
	}, []);

	const openSonglistWithCallback: ILayoutOpenSonglistWithCallback = (
		callbackFn,
		config,
	) => {
		// If the line below looks weird check https://medium.com/swlh/how-to-store-a-function-with-the-usestate-hook-in-react-8a88dd4eede1
		setSonglistCallbackFn(() => callbackFn);
		setSonglistConfig(config);
		setOpenedSonglist(true);
	};

	const openSetlistWithCallback: ILayoutOpenSetlistWithCallback = (
		callbackFn,
		config,
	) => {
		// If the line below looks weird check https://medium.com/swlh/how-to-store-a-function-with-the-usestate-hook-in-react-8a88dd4eede1
		setSetlistCallbackFn(() => callbackFn);
		setSetlistConfig(config);
		setOpenedSetlist(true);
	};

	const openSetlistGenerate = (
		config: GenerateSettingsBottomPanelProps['config'],
	) => {
		setGenerateSetlistConfig(config);
		setOpenedGenerateSetlist(true);
	};

	const openAddSongs = (config?: OpenAddSongsConfig) => {
		setAddSongsConfig(config);
		setOpenedAddSongs(true);
		fireOpenAddSongsEvent();
	};

	const openAddCustomSong = () => {
		setOpenedAddCustomSong(true);
		fireOpenAddCustomSongEvent();
	};

	const openImportCsv = () => {
		setOpenedImportCsv(true);
	};

	const openQuickAddSongs = () => {
		setOpenedQuickAddSongs(true);
		fireOpenQuickAddSongsEvent();
	};

	const openBottomCard = (config: BottomCardConfig) => {
		setOpenedBottomCard(true);
		setBottomCardConfig(config);
	};

	const openSupportModal = () => {
		setOpenedSupportModal(true);
	};

	const shootFireworks = useCallback(() => {
		controller?.current?.run({ speed: 1 });
		setTimeout(() => {
			openBottomCard?.({
				title: 'Nice work!',
				text: 'Now that you know the basics, import your song library and start creating set lists for your team.',
			});
		}, 500);
		setTimeout(() => {
			controller?.current?.pause();
		}, 4000);
	}, []);

	const onInitHandler = ({ conductor }) => {
		controller.current = conductor;
	};

	return (
		<LayoutContext.Provider
			value={{
				setAccountOpen,
				openSong,
				openSonglistWithCallback,
				setHideBottomMenu,
				openSetlistWithCallback,
				openSetlistGenerate,
				openAddSongs,
				openAddCustomSong,
				openImportCsv,
				openQuickAddSongs,
				openBottomCard,
				shootFireworks,
				openSupportModal,
				hideBottomMenu,
				scrollTop,
			}}
		>
			{children}

			{ReactDOM.createPortal(
				<Fireworks
					onInit={onInitHandler}
					style={{
						position: 'fixed',
						pointerEvents: 'none',
						width: '100%',
						height: '100%',
						top: 0,
						left: 0,
						zIndex: 9999,
					}}
				/>,
				document.body,
			)}

			{/* Panels */}
			{openedSong &&
				(!!songConfig?.songId || !!songConfig?.teamSongId) && (
					<SongModalPanel
						config={songConfig}
						setOpen={setOpenedSong}
					/>
				)}
			{openedSonglist && (
				<SonglistModalPanel
					setOpen={setOpenedSonglist}
					callbackFn={songlistCallbackFn}
					openSong={openSong}
					config={songlistConfig}
				/>
			)}
			{openedSetlist && setlistConfig && (
				<GenerateSetlist
					setOpen={setOpenedSetlist}
					callbackFn={setlistCallbackFn}
					config={setlistConfig}
					openSonglistWithCallback={openSonglistWithCallback}
					openSong={openSong}
					setAccountOpen={setAccountOpen}
					openBottomCard={openBottomCard}
				/>
			)}
			{openedSupportModal && (
				<SupportUsModal setOpen={setOpenedSupportModal} />
			)}
			{accountOpen.open && (
				<AccountPanel
					open={true}
					defaultDisplayState={accountOpen.defaultDisplayState}
					setOpen={setAccountOpen}
				/>
			)}
			{openedGenerateSetlist && (
				<GenerateSettingsBottomPanel
					openSonglistWithCallback={openSonglistWithCallback}
					setAccountOpen={setAccountOpen}
					config={generateSetlistConfig}
					onClose={() => {
						setOpenedGenerateSetlist(false);
						fireCloseGenerateSetlistBottomPanel();
					}}
					onSave={(updatedGenerateSettings, generateOnOpen) => {
						fireNavigateGenerateSetlist({
							hasBibleReference:
								!!updatedGenerateSettings.bibleReference,
							hasSongThemes: !!updatedGenerateSettings.searchTerm,
							totalSongPositions:
								updatedGenerateSettings.totalSongPositions,
							postSermonSongPosition:
								updatedGenerateSettings.postSermonSongPosition ||
								undefined,
						});
						setOpenedGenerateSetlist(false);
						openSetlistWithCallback(
							({ fromOnboarding }) => {
								if (fromOnboarding) {
									openBottomCard({
										title: 'Set list created',
										text: "Set lists move from 'Upcoming' to 'Live' as they're played, then to 'Complete' for feedback. Asaph will request feedback for sets you've created or received. The set list you just created is marked as 'Complete' so you can give feedback.",
										component: (
											<video
												autoPlay
												playsInline
												muted
												loop
												src="/assets/videos/onboarding.mp4"
												style={{
													width: '100%',
													borderRadius: 8,
												}}
											/>
										),
										callbackFn: () => {
											shootFireworks();
										},
									});
								}
							},
							{
								editOnOpen: true,
								generateSettings: updatedGenerateSettings,
								selectedSongIds:
									updatedGenerateSettings.selectedSongIds,
								generateOnOpen,
								fromOnboarding:
									generateSetlistConfig?.fromOnboarding,
							},
						);
					}}
				/>
			)}
			{openedAddSongs && (
				<AddSongs
					onClose={(addedSongIds) => {
						setOpenedAddSongs(false);
						addSongsConfig?.callbackFn?.(addedSongIds);
					}}
					onCustomSongOpen={() => {
						setOpenedAddSongs(false);
						openAddCustomSong();
					}}
					config={addSongsConfig}
					openSong={openSong}
				/>
			)}
			{openedAddCustomSong && (
				<AddCustomSong onClose={() => setOpenedAddCustomSong(false)} />
			)}
			{openedImportCsv && (
				<ImportCsv onClose={() => setOpenedImportCsv(false)} />
			)}
			{openedBottomCard ? (
				<BottomCard
					title={bottomCardConfig?.title}
					onClose={() => {
						setOpenedBottomCard(false);
						bottomCardConfig?.callbackFn?.();
					}}
				>
					<BottomCardForm>
						{bottomCardConfig?.component
							? bottomCardConfig.component
							: null}
						<RenderRichText
							className={classNames([
								styles['bottom-card-text'],
								bottomCardConfig?.component && 'mt-sm',
							])}
							text={bottomCardConfig?.text || ''}
						/>
					</BottomCardForm>
					<BottomCardAction>
						<AsaphButton
							onClick={() => {
								setOpenedBottomCard(false);
								bottomCardConfig?.callbackFn?.();
							}}
							fullWidth
							label="OK, got it"
							color="blue"
						/>
					</BottomCardAction>
				</BottomCard>
			) : null}
			{openedQuickAddSongs && (
				<QuickAddSongsBottomPanel
					onClose={() => {
						setOpenedQuickAddSongs(false);
					}}
					openAddSongs={openAddSongs}
					openImportCsv={openImportCsv}
				/>
			)}
		</LayoutContext.Provider>
	);
};

export default LayoutProvider;
